export class PcmPlayer {
  private context: AudioContext | null = null;
  private gain: GainNode | null = null;
  private nextStart = 0;
  private sources = new Set<AudioBufferSourceNode>();
  private volume = 0.82;

  async start(): Promise<void> {
    if (!this.context) {
      this.context = new AudioContext({ latencyHint: "interactive" });
      this.gain = this.context.createGain();
      this.gain.gain.value = this.volume;
      this.gain.connect(this.context.destination);
    }
    if (this.context.state === "suspended") {
      await this.context.resume();
    }
    this.nextStart = Math.max(this.nextStart, this.context.currentTime);
  }

  async enqueue(pcm: ArrayBuffer, sampleRate: number): Promise<void> {
    await this.start();
    const context = this.context;
    if (!context) return;

    const input = new Int16Array(pcm);
    const buffer = context.createBuffer(1, input.length, sampleRate);
    const channel = buffer.getChannelData(0);
    for (let index = 0; index < input.length; index += 1) {
      channel[index] = input[index] / 32768;
    }

    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(this.gain ?? context.destination);
    source.onended = () => this.sources.delete(source);
    this.sources.add(source);

    const startAt = Math.max(context.currentTime + 0.015, this.nextStart);
    source.start(startAt);
    this.nextStart = startAt + buffer.duration;
  }

  setVolume(percent: number): void {
    this.volume = Math.max(0.01, Math.min(1, percent / 100));
    if (this.gain && this.context) {
      this.gain.gain.setTargetAtTime(this.volume, this.context.currentTime, 0.015);
    }
  }

  stop(): void {
    for (const source of this.sources) {
      try {
        source.stop();
      } catch {
        // Источник уже мог завершиться между проверкой и остановкой.
      }
    }
    this.sources.clear();
    if (this.context) this.nextStart = this.context.currentTime;
  }

  async close(): Promise<void> {
    this.stop();
    await this.context?.close();
    this.context = null;
    this.gain = null;
  }
}
